import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'

interface ErrorBoundaryProps {
  /** What to render in place of a subtree that threw while rendering. */
  fallback: ReactNode
  children: ReactNode
}

/**
 * A RENDER FAULT COSTS THE SUBTREE THAT FAULTED, NOT THE PAGE.
 *
 * <p>React 18 unmounts the WHOLE ROOT on an uncaught render error. With no boundary anywhere, one
 * answered clarify card whose `answer` was a string rather than an array blanked every engagement
 * that had one — the page painted, the frame folded in, and `#root` went empty with nothing on
 * screen to say why. A card that cannot be drawn is a card; a white page is an outage.</p>
 *
 * <p>A CLASS, because it has to be: no hook catches a render error.</p>
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, { failed: boolean }> {
  override state = { failed: false }

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // STILL REPORTED. Contained is not the same as fixed, and the console is where it was found.
    console.error(error, info.componentStack)
  }

  override render(): ReactNode {
    return this.state.failed ? this.props.fallback : this.props.children
  }
}
